import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { categories } from "@/mocks/videos";

const navItems = [
  { label: "首页", path: "/" },
  ...categories.slice(0, 4).map((c) => ({ label: c.name, path: `/category/${c.id}` })),
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur shadow-[0_1px_0_rgba(0,0,0,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="inline-flex w-9 h-9 md:w-10 md:h-10 items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-fire-fill text-lg md:text-xl"></i>
            </span>
            <span className="font-heading text-xl md:text-2xl font-black text-foreground-950 tracking-wide">
              吃瓜<span className="text-primary-600">视频</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  location.pathname === item.path
                    ? "bg-primary-100 text-primary-700 font-bold"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/rank"
              className="px-4 py-2 rounded-full text-sm whitespace-nowrap text-foreground-700 hover:bg-background-200 flex items-center gap-1"
            >
              <i className="ri-trophy-line"></i> 排行榜
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={onSearch} className="relative">
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索吃瓜视频..."
                className="w-52 pl-9 pr-3 py-2 rounded-full bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            </form>
            <Link
              to="/about"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              关于我们
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden inline-flex w-10 h-10 items-center justify-center rounded-full bg-background-100 text-foreground-800"
            aria-label="打开菜单"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden pb-4 pt-2">
            <form onSubmit={onSearch} className="relative mb-3">
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索吃瓜视频..."
                className="w-full pl-9 pr-3 py-2 rounded-full bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-400"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            </form>
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2.5 rounded-lg text-sm whitespace-nowrap ${
                    location.pathname === item.path
                      ? "bg-primary-100 text-primary-700 font-bold"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/rank"
                className="px-4 py-2.5 rounded-lg text-sm whitespace-nowrap text-foreground-700 hover:bg-background-200"
              >
                排行榜
              </Link>
              <Link
                to="/about"
                className="px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm whitespace-nowrap text-center"
              >
                关于我们
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}